import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { catchError, of, switchMap, tap, timer } from 'rxjs';
import { ChargepointType } from '../../../api/chargepoints-api.service';
import {
  IvyTransaction,
  OCPPTransaction,
  OkeChargeTransaction,
  Transaction,
  TransactionApiService,
  TransactionStatus,
} from '../../../api/transactions-api.service';
import { CopiableInfoComponent } from '../../../elements/copiable-info/copiable-info.component';
import { JsonViewerService } from '../../common/json-viewer-dialog/json-viewer.service';
import { TransactionStatusComponent } from '../transaction-status/transaction-status.component';

export interface TransactionMonitorDialogData {
  transactionId: string;
}

const REFRESH_INTERVAL_MS = 3000;

@Component({
  selector: 'app-transaction-monitor-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatTooltipModule,
    CopiableInfoComponent,
    TransactionStatusComponent,
  ],
  templateUrl: './transaction-monitor-dialog.component.html',
  styleUrls: ['./transaction-monitor-dialog.component.scss'],
})
export class TransactionMonitorDialogComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject(MatDialogRef<TransactionMonitorDialogComponent>);
  private readonly transactionApiService = inject(TransactionApiService);
  private readonly snackBar = inject(MatSnackBar);
  readonly jsonViewerService = inject(JsonViewerService);
  readonly data = inject<TransactionMonitorDialogData>(MAT_DIALOG_DATA);

  readonly chargepointType = ChargepointType;

  transaction: Transaction | null = null;
  loading = true;
  fetchingError = false;
  stopping = false;

  ngOnInit(): void {
    timer(0, REFRESH_INTERVAL_MS).pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(() => this.transactionApiService.getTransactionById(this.data.transactionId).pipe(
        catchError(() => {
          this.fetchingError = true;
          this.loading = false;
          return of(null);
        })
      )),
      tap((transaction) => {
        if (!transaction) {
          return;
        }
        this.transaction = transaction;
        this.loading = false;
        this.fetchingError = false;
      }),
    ).subscribe();
  }

  close(): void {
    this.dialogRef.close();
  }

  stopTransaction(): void {
    if (!this.transaction || this.stopping) {
      return;
    }
    this.stopping = true;
    this.transactionApiService.stopTransaction(this.transaction.id).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: () => {
        this.snackBar.open('Stop transaction requested', 'Close', { duration: 3000 });
        this.stopping = false;
      },
      error: () => {
        this.snackBar.open('Failed to stop transaction', 'Close', { duration: 3000 });
        this.stopping = false;
      },
    });
  }

  canStopTransaction(): boolean {
    return this.transaction?.status === TransactionStatus.Started;
  }

  asOcpp(transaction: Transaction): OCPPTransaction {
    return transaction as OCPPTransaction;
  }

  asOkecharge(transaction: Transaction): OkeChargeTransaction {
    return transaction as OkeChargeTransaction;
  }

  asIvy(transaction: Transaction): IvyTransaction {
    return transaction as IvyTransaction;
  }

  formatDate(value?: Date): string {
    return value ? value.toLocaleString() : '—';
  }
}
